import { StateRegistrationCitizenship } from 'mutation/constants/state-registration-citizenship';
import {
  OpenLegisVM,
  ToDPRVM,
  ChoiceItalianVM,
  foreignCitizenshipVM,
  DoubleCitizenshipVM,
  ItalianCitizenshipDPVM,
  OpeLegisVM,
  ForeignNaturalizationVM,
  RenunciationCitizenshipVM,
  RegistrationCitizenshipFormVM,
} from 'mutation/view-models/registration-citizenship';
import { ActType } from 'shell/constants/act-type';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';
import { Article } from 'shell/models/article';
import { Authority } from 'shell/models/authority';
import { RegistrationCitizenshipMutation } from 'shell/models/mutation/registration-citizenship-mutation';
import { Citizenship } from 'shell/models/person/citizenship';
import { Document } from 'shell/models/person/document';
import { DocumentANSC } from 'shell/models/person/document-ANSC';
import { transformDocumentDigital } from 'shell/utils/consult-person/request-transformer/shared/transform-document-digital';

export const transformerRequestRegistrationCitizenshipMutation = (
  values: RegistrationCitizenshipFormVM
): RegistrationCitizenshipMutation => {
  let dataCitizenship:
    | OpenLegisVM
    | ToDPRVM
    | ChoiceItalianVM
    | foreignCitizenshipVM
    | DoubleCitizenshipVM
    | ItalianCitizenshipDPVM
    | OpeLegisVM
    | ForeignNaturalizationVM
    | RenunciationCitizenshipVM
    | null = null;

  switch (values.newCitizenship?.reasonCitizenship) {
    case StateRegistrationCitizenship.OpenLegis:
      dataCitizenship = values.newCitizenship?.openLegis || {};
      break;
    case StateRegistrationCitizenship.ToDPR:
      dataCitizenship = values.newCitizenship?.toDPR || {};
      break;
    case StateRegistrationCitizenship.ChoiceItalian:
      dataCitizenship = values.newCitizenship?.choiceItalian || {};
      break;
    case StateRegistrationCitizenship.ForeignCitizenship:
      dataCitizenship = values.newCitizenship?.foreignCitizenship || {};
      break;
    case StateRegistrationCitizenship.OpeLegis:
      dataCitizenship = values.newCitizenship?.opeLegis || {};
      break;
    case StateRegistrationCitizenship.ForeignNaturalization:
      dataCitizenship = values.newCitizenship?.foreignNaturalization || {};
      break;
    case StateRegistrationCitizenship.RenunciationCitizenship:
      dataCitizenship = values.newCitizenship?.renunciationCitizenship || {};
      break;
    case StateRegistrationCitizenship.DoubleCitizenship:
      dataCitizenship = values.newCitizenship?.doubleCitizenship || {};
      break;
    case StateRegistrationCitizenship.ItalianCitizenshipDP:
      dataCitizenship = values.newCitizenship?.italianCitizenshipDP || {};
      break;
    default:
      dataCitizenship = {};
      break;
  }

  let cittadinanza: Array<Citizenship>;

  if (
    values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.OpenLegis ||
    values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.ToDPR ||
    values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian
  ) {
    cittadinanza = [
      {
        dataValidita:
          (dataCitizenship as ChoiceItalianVM)?.dateBuying?.toFormat('yyyy-MM-dd') ||
          (dataCitizenship as OpenLegisVM)?.dateForm?.toFormat('yyyy-MM-dd') ||
          undefined,
        codiceStato: CODE_STATE_ITALIAN, // fixed
        noteStato: (dataCitizenship as ChoiceItalianVM)?.note || null,
        descrizioneStato: 'ITALIA', // fixed
      },
    ];
  } else {
    cittadinanza = [
      {
        dataValidita:
          (dataCitizenship as foreignCitizenshipVM)?.dateBuying?.toFormat('yyyy-MM-dd') ||
          (dataCitizenship as DoubleCitizenshipVM)?.dateFrom?.toFormat('yyyy-MM-dd') ||
          (dataCitizenship as RenunciationCitizenshipVM)?.dateLoss?.toFormat('yyyy-MM-dd') ||
          undefined,
        codiceStato:
          (dataCitizenship as foreignCitizenshipVM)?.otherCitizenship?.id ||
          (dataCitizenship as DoubleCitizenshipVM)?.citizenship?.id ||
          undefined,
        noteStato: (dataCitizenship as foreignCitizenshipVM)?.note || null,
        descrizioneStato:
          (dataCitizenship as foreignCitizenshipVM)?.otherCitizenship?.value ||
          (dataCitizenship as DoubleCitizenshipVM)?.citizenship?.value ||
          undefined,
      },
    ];
  }

  let attoCittadinanza: Document | DocumentANSC | undefined = undefined;

  if ((dataCitizenship as ToDPRVM)?.actType === ActType.AttoCartaceo) {
    attoCittadinanza = {
      comuneRegistrazione:
        (dataCitizenship as ToDPRVM)?.actType === ActType.AttoCartaceo
          ? {
              nomeComune:
                (dataCitizenship as ToDPRVM)?.paper?.actMunicipality?.description || undefined,
              codiceIstat: (dataCitizenship as ToDPRVM)?.paper?.actMunicipality?.id || undefined,
              siglaProvinciaIstat:
                (dataCitizenship as ToDPRVM)?.paper?.actMunicipality?.province || undefined,
              descrizioneLocalita:
                (dataCitizenship as ToDPRVM)?.paper?.actMunicipality?.description || undefined,
            }
          : undefined,
      anno: (dataCitizenship as ToDPRVM)?.paper?.actYear || undefined,
      parte: (dataCitizenship as ToDPRVM)?.paper?.actPart || undefined,
      serie: (dataCitizenship as ToDPRVM)?.paper?.actSeries || undefined,
      numeroAtto: (dataCitizenship as ToDPRVM)?.paper?.actNumber || undefined,
      volume: (dataCitizenship as ToDPRVM)?.paper?.actVolume || undefined,
      dataFormazioneAtto:
        (dataCitizenship as ToDPRVM)?.paper?.actDate?.toFormat('yyyy-MM-dd') || undefined,
      trascritto: (dataCitizenship as ToDPRVM)?.paper?.transcribed ? '1' : '0',
    };
  } else if ((dataCitizenship as ToDPRVM)?.actType === ActType.AttoDigitale) {
    attoCittadinanza = transformDocumentDigital((dataCitizenship as ToDPRVM)?.digital || undefined);
  }

  const isForeign = (dataCitizenship as ToDPRVM)?.isForeign || false;

  const autorita: Authority = {
    nome: isForeign
      ? (dataCitizenship as ToDPRVM)?.foreign?.foreignState?.value || undefined
      : (dataCitizenship as ToDPRVM)?.italian?.municipality?.description || undefined,
    descrizione: isForeign
      ? (dataCitizenship as ToDPRVM)?.foreign?.foreignState?.value || undefined
      : (dataCitizenship as ToDPRVM)?.italian?.municipality?.description || undefined,
    provincia: isForeign
      ? (dataCitizenship as ToDPRVM)?.foreign?.foreignState?.province || undefined
      : (dataCitizenship as ToDPRVM)?.italian?.municipality?.province || undefined,
  };

  const tipoComuneAutorita = isForeign ? '1' : '0';

  const articolo: Article = {
    descrizioneBreve:
      (dataCitizenship as ToDPRVM)?.article?.el?.descrizioneBreve ||
      (dataCitizenship as ToDPRVM)?.article?.value ||
      undefined,
    key: {
      subEvento: (dataCitizenship as ToDPRVM)?.article?.subEvent?.toString() || undefined,
    },
  };

  return {
    testataRichiesta: {
      dataDecorrenza: values.editDataControl?.dateValidity?.toFormat('yyyy-MM-dd') || undefined,
    },
    generalitaPerRicerca: {
      codiceFiscale: {
        codFiscale: values.person?.fiscalCode || undefined,
      },
      nome: values.person?.name || undefined,
      cognome: values.person?.surname || undefined,
      senzaCognome: values.person?.withoutSurname ? '1' : undefined,
      senzaNome: values.person?.withoutSurname ? '1' : undefined,
      idSchedaSoggettoANPR: values.person?.idSchedaSoggettoANPR || undefined,
    },
    cittadinanza,
    datiControllo: {
      tipoMutazione: ['02'],
      gestioneCF: '0',
    },
    dataDprDm: (dataCitizenship as ToDPRVM)?.dateDPR?.toFormat('yyyy-MM-dd') || undefined,
    dataAutorita: (dataCitizenship as ToDPRVM)?.dateOath?.toFormat('yyyy-MM-dd') || undefined,
    autorita,
    tipoComuneAutorita,
    attoCittadinanzaANPR: attoCittadinanza
      ? {
          atto:
            (dataCitizenship as ToDPRVM)?.actType === ActType.AttoCartaceo
              ? attoCittadinanza
              : undefined,
          attoANSC:
            (dataCitizenship as ToDPRVM)?.actType === ActType.AttoDigitale
              ? attoCittadinanza
              : undefined,
        }
      : undefined,

    articolo,
  };
};
